import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import DeviceLoadingSkeleton from '../../devices/shared-ui/deviceLoadingSkeleton';
import { formatDeviceMetric } from '../../../utils/formatters';
import {
  useEnergyConsumerBuildingDetailQuery,
  useEnergyConsumerBuildingDetailUpdatesSubscription,
} from '../../../../types/generated-types';
import { PanelFrame, ViewMode } from '../../devices/shared-ui/panel-frame';
import moment from 'moment';
import { DeviceInformation } from '../../devices/shared-ui/device-info';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';
import { EnergyControllerComponent } from '../../devices/virtual-power-plant/energy-controller';
import { EnergyInverterComponent } from '../../devices/virtual-power-plant/energy-inverter';
import { EnergyStorageCoolingSystemComponent } from '../../devices/virtual-power-plant/energy-storage-cooling-system';
import { FireSuppressionSystemComponent } from '../../devices/virtual-power-plant/fire-suppression-system';
import { EnergyConverterComponent } from '../../devices/virtual-power-plant/energy-converter';
import { EnergySwitchPanelCircuitComponent } from '../../devices/virtual-power-plant/energy-switch-panel-circuit';
import { EnergyMeterComponent } from '../../devices/virtual-power-plant/energy-meter';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';

interface EnergyConsumerBuildingProps {
  id: string;
}

export const EnergyConsumerBuildingComponent: React.FC<
  EnergyConsumerBuildingProps
> = ({ id }) => {
  const [participant, setParticipant] = React.useState<any>();
  const { data, loading, error } = useEnergyConsumerBuildingDetailQuery({
    variables: { id },
  });

  useEnergyConsumerBuildingDetailUpdatesSubscription({
    variables: { id },
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  React.useEffect(() => {
    if (data?.energyConsumerBuildingById) {
      setParticipant(data.energyConsumerBuildingById);
    }
  }, [data]);

  if (loading) return <DeviceLoadingSkeleton size="large" />;
  if (error)
    return (
      <Typography color="error">Error loading participant data</Typography>
    );
  if (!participant) return null;

  const participantInfo: DeviceInformation[] = [
    {
      'Building Name': participant.name || 'Unnamed Building',
      Location: participant.location || 'Unknown',
      'Last Updated': moment(participant.timestamp).fromNow(),
      'Power State': participant.powerState?.value ? 'On' : 'Off',
      'Connection Status': participant.connected?.value
        ? 'Connected'
        : 'Disconnected',
      'Last Connected': moment(participant.lastConnectedAt?.value).fromNow(),
      'Tariff Rate': formatDeviceMetric({
        value: participant.tariffRateSell?.value ?? undefined,
        units: participant.tariffRateSell?.units ?? undefined,
      }),
      Occupancy: formatDeviceMetric({
        value: participant.occupancy?.value ?? undefined,
        units: participant.occupancy?.units ?? undefined,
      }),
      'Energy Efficiency Score': formatDeviceMetric({
        value: participant.energyEfficiencyScore?.value ?? undefined,
        units: participant.energyEfficiencyScore?.units ?? undefined,
      }),
    },
  ];

  return (
    <PanelFrame
      title="Energy Consumer Building"
      iconType="material-symbols:business"
      panel={participant}
      infoEntries={participantInfo}
      viewMode={ViewMode.NORMAL}
      editingEnabled={false}
    >
      <Box sx={{ p: 2 }}>
        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          divider={<Divider orientation="vertical" flexItem />}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Building Status
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Power State
                  </Typography>
                  <Typography variant="body1">
                    {participant.powerState?.value ? 'On' : 'Off'}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Connection Status
                  </Typography>
                  <Typography variant="body1">
                    {participant.connected?.value
                      ? 'Connected'
                      : 'Disconnected'}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Last Connected
                  </Typography>
                  <Typography variant="body1">
                    {moment(participant.lastConnectedAt?.value).fromNow()}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Energy Metrics
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Tariff Rate
                  </Typography>
                  <Typography variant="body1">
                    {formatDeviceMetric({
                      value: participant.tariffRateSell?.value ?? undefined,
                      units: participant.tariffRateSell?.units ?? undefined,
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Occupancy
                  </Typography>
                  <Typography variant="body1">
                    {formatDeviceMetric({
                      value: participant.occupancy?.value ?? undefined,
                      units: participant.occupancy?.units ?? undefined,
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Energy Efficiency Score
                  </Typography>
                  <Typography variant="body1">
                    {formatDeviceMetric({
                      value:
                        participant.energyEfficiencyScore?.value ?? undefined,
                      units:
                        participant.energyEfficiencyScore?.units ?? undefined,
                    })}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          {participant.energySwitchPanelCircuitId ? (
            <EnergySwitchPanelCircuitComponent
              id={participant.energySwitchPanelCircuitId}
            />
          ) : null}
          {participant.meterId ? (
            <EnergyMeterComponent id={participant.meterId} />
          ) : null}
          {participant.inverterId ? (
            <EnergyInverterComponent id={participant.inverterId} />
          ) : null}
          {participant.converterId ? (
            <EnergyConverterComponent id={participant.converterId} />
          ) : null}
        </Stack>
      </Box>
    </PanelFrame>
  );
};
